'use client';

/**
 * BriefOverlay Component
 *
 * Full-viewport executive briefing surface triggered from the business-portal
 * topbar (Brief view button or ⌘B). Provides a 30-second snapshot of today:
 *   - Greeting + live timestamp header with a `close` affordance.
 *   - KPI grid (3–6 tiles): label / value / optional delta.
 *   - Top 3 priority signals (severity-coded list).
 *   - 3 quick actions (icon buttons that route to common surfaces).
 *   - "Cases needing review" list (top 3) with clickable rows that route to
 *     the case detail.
 *
 * Reference: `.claude/design-handoff-v3/project/pages/auth-overlays.jsx`
 * `OverlayBrief` (full-viewport variant of `BriefViewPopover`).
 *
 * Architectural notes:
 *   - Uses MUI `Modal` rather than `SplitPanel` per the A4 brief: this is a
 *     temporary, dismissable overlay, NOT a persistent rail.
 *   - Backdrop blur matches the design's `radial-gradient` overlay (mint @ low
 *     alpha) without painting any custom dimming layer.
 *   - Esc and backdrop-click both close.
 *   - Typography stays within the §11b2 ceiling — `h4` (20px) is the headline
 *     since the overlay is a hero surface (page-takeover); KPI values use
 *     `h4` as well, NEVER larger than the host page's `h5` title would be.
 */

import BoltIcon from '@mui/icons-material/Bolt';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardCommandKeyIcon from '@mui/icons-material/KeyboardCommandKey';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ReactNode, useId } from 'react';
import { useI18n } from '../../hooks';
import { iconSizes } from '../../theme/typography';
import { GlassButton } from '../GlassButton';
import { GlassChip } from '../GlassChip';
import { GlassIconButton } from '../GlassIconButton';

// ============================================================================
// Types
// ============================================================================

export interface BriefOverlayKpi {
  /** Short uppercase label (e.g., "To act on"). */
  label: string;
  /** Display value — number or pre-formatted string. */
  value: string | number;
  /**
   * Optional delta description ("by 11am", "+12% vs Tue").
   * Empty/undefined = no secondary line.
   */
  delta?: string;
  /** Severity tone for the value + delta. Default `neutral`. */
  tone?: 'mint' | 'amber' | 'red' | 'neutral';
}

export interface BriefOverlaySignal {
  /** Short text (≤120 chars). */
  text: string;
  /** Visual severity. */
  severity: 'critical' | 'warning' | 'info';
  /** Optional destination — when set the row is clickable. */
  href?: string;
  /** Optional click handler. Either `href` or `onClick` makes the row interactive. */
  onClick?: () => void;
}

export interface BriefOverlayQuickAction {
  /** Localized action label. */
  label: string;
  /** Click handler. */
  onClick: () => void;
  /** Optional icon (rendered to the left of the label). */
  icon?: ReactNode;
}

export interface BriefOverlayCase {
  /** Primary title (e.g., loan number or property). */
  title: string;
  /** Secondary line. */
  sub: string;
  /** Destination URL or path. */
  href: string;
  /** Optional click handler — when omitted, navigation is the caller's concern. */
  onClick?: () => void;
}

export interface BriefOverlayProps {
  /** Whether the overlay is open. */
  open: boolean;
  /** Close handler — fires on Esc, backdrop click, and the X button. */
  onClose: () => void;
  /** Display name of the user being briefed (e.g., "Rachel"). */
  userName?: string;
  /** Optional headline (defaults to localized "Brief view"). */
  headline?: string;
  /** Pre-rendered intro paragraph (allows mint-bold lead-in markup). */
  intro?: ReactNode;
  /** KPI tiles (3–6 recommended). */
  kpis: BriefOverlayKpi[];
  /** Top priority signals (max 3 rendered). */
  signals: BriefOverlaySignal[];
  /** Quick actions (3 recommended). */
  quickActions: BriefOverlayQuickAction[];
  /** Top cases needing review (3 recommended). */
  cases: BriefOverlayCase[];
  /** Optional empty-state for the signals list. */
  signalsEmpty?: ReactNode;
  /** Optional empty-state for the cases list. */
  casesEmpty?: ReactNode;
  /** Style overrides on the inner Paper. */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Helpers
// ============================================================================

const MONO = 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace';

/**
 * Format the header timestamp as `Wed May 14 · 9:38a`. US-style by design
 * (matches the design handoff spec).
 */
function formatLiveAt(date: Date): string {
  const datePart = date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  const mm = minutes.toString().padStart(2, '0');
  const suffix = hours < 12 ? 'a' : 'p';
  return `${datePart} · ${h12}:${mm}${suffix}`;
}

// ============================================================================
// Component
// ============================================================================

export function BriefOverlay({
  open,
  onClose,
  userName,
  headline,
  intro,
  kpis,
  signals,
  quickActions,
  cases,
  signalsEmpty,
  casesEmpty,
  sx,
}: BriefOverlayProps) {
  const theme = useTheme();
  const { t } = useI18n();
  const titleId = useId();
  const isDark = theme.palette.mode === 'dark';

  const mint = theme.palette.success.main;
  const amber = theme.palette.warning.main;
  const red = theme.palette.error.main;
  const mintBorder = alpha(mint, isDark ? 0.22 : 0.18);
  const mintInsetGlow = alpha(mint, isDark ? 0.06 : 0.05);

  const tonePalette: Record<NonNullable<BriefOverlayKpi['tone']>, string> = {
    mint,
    amber,
    red,
    neutral: theme.palette.text.primary,
  };
  const severityPalette: Record<BriefOverlaySignal['severity'], string> = {
    critical: red,
    warning: amber,
    info: mint,
  };

  const resolvedHeadline = headline ?? t('overlays.brief.title');
  const visibleSignals = signals.slice(0, 3);
  const visibleCases = cases.slice(0, 3);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      slotProps={{
        backdrop: {
          // Backdrop has a faint mint cast that ties the overlay to the
          // header trigger (matches the design handoff). The blur is
          // GPU-cheap (no extra layer beyond MUI's existing backdrop).
          sx: {
            backgroundColor: alpha(theme.palette.background.default, 0.66),
            backdropFilter: 'blur(8px) saturate(140%)',
            WebkitBackdropFilter: 'blur(8px) saturate(140%)',
          },
        },
      }}
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'center',
        p: { xs: 2, md: 4 },
        // Pin to the viewport, NOT to the layout column.
        zIndex: theme.zIndex.modal,
      }}
    >
      <Paper
        elevation={0}
        role="dialog"
        aria-labelledby={titleId}
        sx={[
          {
            // Full-viewport "executive summary" shape: wide hero, tall enough
            // to host KPI grid + two list sections without scroll on a
            // standard laptop, scrolls gracefully on smaller viewports.
            width: '100%',
            maxWidth: 1100,
            maxHeight: { xs: '100%', md: 'calc(100vh - 64px)' },
            display: 'flex',
            flexDirection: 'column',
            borderRadius: { xs: '12px', md: '14px' },
            overflow: 'hidden',
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: mintBorder,
            boxShadow: `${theme.shadows[16]}, inset 0 0 0 1px ${mintInsetGlow}`,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <BoltIcon aria-hidden sx={{ fontSize: iconSizes.md, color: 'success.main' }} />
            <Typography
              id={titleId}
              variant="h4"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {userName ? `${resolvedHeadline} · ${userName}` : resolvedHeadline}
            </Typography>
            <GlassChip
              label={t('overlays.brief.live')}
              size="small"
              color="success"
              aria-label={t('overlays.brief.live')}
              sx={{ height: 20 }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
            <Typography
              variant="caption"
              sx={{ fontFamily: MONO, color: 'text.disabled' }}
              aria-label={t('overlays.brief.liveAtAria')}
            >
              {formatLiveAt(new Date())}
            </Typography>
            <Box
              aria-hidden
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 0.5,
                px: 0.75,
                py: 0.25,
                borderRadius: '6px',
                border: '1px solid',
                borderColor: 'divider',
                color: 'text.disabled',
              }}
            >
              <KeyboardCommandKeyIcon sx={{ fontSize: iconSizes['2xs'] }} />
              <Typography variant="overline" sx={{ fontFamily: MONO, letterSpacing: '0.08em', lineHeight: 1 }}>
                B
              </Typography>
            </Box>
            <GlassIconButton size="small" onClick={onClose} aria-label={t('overlays.brief.close')} sx={{ ml: 0.5 }}>
              <CloseRoundedIcon sx={{ fontSize: iconSizes.md }} />
            </GlassIconButton>
          </Box>
        </Box>

        {/* Body — single scrollable region */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2.5 }}>
          {intro && (
            <Typography
              variant="body1"
              component="p"
              sx={{ color: 'text.primary', lineHeight: 1.6, mb: 2.5, maxWidth: 820 }}
            >
              {intro}
            </Typography>
          )}

          {/* KPI Grid */}
          {kpis.length > 0 && (
            <Box
              role="group"
              aria-label={t('overlays.brief.kpisLabel')}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: `repeat(${Math.min(kpis.length, 3)}, 1fr)`,
                  md: `repeat(${Math.min(kpis.length, 6)}, 1fr)`,
                },
                gap: 1.5,
                mb: 3,
              }}
            >
              {kpis.map((kpi) => (
                <KpiTile key={kpi.label} kpi={kpi} toneColor={tonePalette[kpi.tone ?? 'neutral']} />
              ))}
            </Box>
          )}

          {/* Two-column section: Signals + Cases */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
              gap: { xs: 2.5, md: 3 },
              alignItems: 'start',
            }}
          >
            {/* Signals */}
            <Box>
              <SectionHeader title={t('overlays.brief.signals.header')} />
              {visibleSignals.length === 0 ? (
                <EmptyHint>{signalsEmpty ?? t('overlays.brief.signals.empty')}</EmptyHint>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {visibleSignals.map((signal, idx) => (
                    <SignalRow
                      key={`${idx}-${signal.text}`}
                      signal={signal}
                      accentColor={severityPalette[signal.severity]}
                    />
                  ))}
                </Box>
              )}

              {/* Quick Actions — sits under signals as the action affordance */}
              {quickActions.length > 0 && (
                <Box sx={{ mt: 2.5 }}>
                  <SectionHeader title={t('overlays.brief.quickActions.header')} />
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: `repeat(${Math.min(quickActions.length, 3)}, 1fr)`,
                      },
                      gap: 1,
                    }}
                  >
                    {quickActions.map((action) => (
                      <GlassButton
                        key={action.label}
                        variant="subtle"
                        size="medium"
                        onClick={action.onClick}
                        startIcon={action.icon}
                        sx={{ justifyContent: 'flex-start', minHeight: 44 }}
                      >
                        <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                          {action.label}
                        </Typography>
                      </GlassButton>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Cases */}
            <Box>
              <SectionHeader title={t('overlays.brief.cases.header')} />
              {visibleCases.length === 0 ? (
                <EmptyHint>{casesEmpty ?? t('overlays.brief.cases.empty')}</EmptyHint>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {visibleCases.map((c) => (
                    <CaseRow key={c.href} caseItem={c} />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
}

BriefOverlay.displayName = 'BriefOverlay';

// ============================================================================
// Sub-components
// ============================================================================

function SectionHeader({ title }: { title: string }) {
  return (
    <Typography
      variant="overline"
      sx={{
        display: 'block',
        fontFamily: MONO,
        color: 'text.disabled',
        letterSpacing: '0.08em',
        fontWeight: 600,
        mb: 1.25,
      }}
    >
      {title}
    </Typography>
  );
}

function EmptyHint({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        py: 2.5,
        px: 1.5,
        textAlign: 'center',
        borderRadius: '7px',
        border: '1px dashed',
        borderColor: 'divider',
        backgroundColor: 'background.default',
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
        {children}
      </Typography>
    </Box>
  );
}

function KpiTile({ kpi, toneColor }: { kpi: BriefOverlayKpi; toneColor: string }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: '8px',
        backgroundColor: 'background.default',
        border: '1px solid',
        borderColor: 'divider',
        minHeight: 76,
      }}
    >
      <Typography
        variant="overline"
        sx={{
          display: 'block',
          fontFamily: MONO,
          color: 'text.disabled',
          letterSpacing: '0.08em',
          fontWeight: 600,
          lineHeight: 1,
          mb: 0.5,
        }}
      >
        {kpi.label}
      </Typography>
      <Typography
        // h4 (20px) is intentionally ≤ the page-title ceiling. h3 would
        // tip into hero territory and violate §11b2 in the host portal.
        variant="h4"
        sx={{
          fontFamily: MONO,
          fontWeight: 600,
          color: toneColor,
          lineHeight: 1.1,
        }}
      >
        {kpi.value}
      </Typography>
      {kpi.delta && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: toneColor === 'inherit' ? 'text.disabled' : alpha(toneColor, 0.85),
            mt: 0.5,
          }}
        >
          {kpi.delta}
        </Typography>
      )}
    </Box>
  );
}

function SignalRow({ signal, accentColor }: { signal: BriefOverlaySignal; accentColor: string }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isClickable = !!signal.onClick || !!signal.href;
  const hoverBg = alpha(accentColor, isDark ? 0.08 : 0.06);

  const handleClick = () => {
    if (signal.onClick) {
      signal.onClick();
      return;
    }
    if (signal.href && typeof window !== 'undefined') {
      window.location.assign(signal.href);
    }
  };

  return (
    <Box
      // Render as <button> only when interactive — a focusable no-op is a
      // screen-reader / keyboard trap.
      {...(isClickable
        ? { component: 'button' as const, type: 'button' as const, onClick: handleClick }
        : { component: 'div' as const })}
      sx={{
        width: '100%',
        px: 1.25,
        py: 1.25,
        borderRadius: '7px',
        backgroundColor: 'background.default',
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '3px solid',
        borderLeftColor: accentColor,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        textAlign: 'left',
        cursor: isClickable ? 'pointer' : 'default',
        fontFamily: 'inherit',
        color: 'inherit',
        transition: 'background-color 120ms ease, border-color 120ms ease',
        minHeight: 44,
        '&:hover': isClickable ? { backgroundColor: hoverBg, borderColor: accentColor } : undefined,
        '&:focus-visible': {
          outline: `2px solid ${accentColor}`,
          outlineOffset: -2,
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.primary',
            lineHeight: 1.45,
          }}
        >
          {signal.text}
        </Typography>
      </Box>
      {isClickable && (
        <ChevronRightIcon aria-hidden sx={{ fontSize: iconSizes.sm, color: 'text.disabled', flexShrink: 0 }} />
      )}
    </Box>
  );
}

function CaseRow({ caseItem }: { caseItem: BriefOverlayCase }) {
  const handleClick = () => {
    if (caseItem.onClick) {
      caseItem.onClick();
      return;
    }
    if (typeof window !== 'undefined') {
      window.location.assign(caseItem.href);
    }
  };

  return (
    <Box
      component="button"
      type="button"
      onClick={handleClick}
      sx={{
        width: '100%',
        px: 1.25,
        py: 1.25,
        borderRadius: '7px',
        backgroundColor: 'background.default',
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        textAlign: 'left',
        cursor: 'pointer',
        fontFamily: 'inherit',
        color: 'inherit',
        transition: 'background-color 120ms ease, border-color 120ms ease',
        minHeight: 44,
        '&:hover': {
          backgroundColor: (theme) => alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.08 : 0.06),
          borderColor: 'success.main',
        },
        '&:focus-visible': {
          outline: (theme) => `2px solid ${theme.palette.success.main}`,
          outlineOffset: -2,
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: 'text.primary',
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {caseItem.title}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'text.secondary',
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {caseItem.sub}
        </Typography>
      </Box>
      <ChevronRightIcon aria-hidden sx={{ fontSize: iconSizes.sm, color: 'text.disabled', flexShrink: 0 }} />
    </Box>
  );
}
